import type { EmploymentType, PublicJobDetail } from '@absurd-careers/shared';
import { SITE } from '~/content/site.js';

/** schema.org employment values differ from ours for two of the five. */
const SCHEMA_EMPLOYMENT_TYPE: Record<EmploymentType, string> = {
  FULL_TIME: 'FULL_TIME',
  PART_TIME: 'PART_TIME',
  CONTRACT: 'CONTRACTOR',
  INTERNSHIP: 'INTERN',
  TEMPORARY: 'TEMPORARY',
};

/**
 * Builds schema.org JobPosting markup. Properties are only emitted when the
 * underlying data exists: an absent salary or an unknown remote-work region is
 * left out rather than guessed at, which is both honest and what the structured
 * data guidelines require.
 */
export function buildJobPostingJsonLd(job: PublicJobDetail, canonicalUrl: string): string {
  const posting: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.descriptionHtml,
    identifier: {
      '@type': 'PropertyValue',
      name: SITE.companyName,
      value: job.slug,
    },
    employmentType: SCHEMA_EMPLOYMENT_TYPE[job.employmentType],
    hiringOrganization: {
      '@type': 'Organization',
      name: SITE.legalName,
      sameAs: SITE.mainSiteUrl,
    },
    url: canonicalUrl,
    directApply: true,
  };

  if (job.publishedAt) posting.datePosted = job.publishedAt.slice(0, 10);
  if (job.closesAt) posting.validThrough = job.closesAt;

  posting.jobLocation = {
    '@type': 'Place',
    address: { '@type': 'PostalAddress', addressLocality: job.locationText },
  };

  if (job.workplaceType === 'REMOTE') {
    posting.jobLocationType = 'TELECOMMUTE';
  }

  const { compensation } = job;
  if (compensation && compensation.currency && (compensation.min != null || compensation.max != null)) {
    const value: Record<string, unknown> = {
      '@type': 'QuantitativeValue',
      unitText: compensation.period ?? 'YEAR',
    };
    if (compensation.min != null && compensation.max != null && compensation.min !== compensation.max) {
      value.minValue = compensation.min;
      value.maxValue = compensation.max;
    } else {
      value.value = compensation.min ?? compensation.max;
    }
    posting.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: compensation.currency,
      value,
    };
  }

  return JSON.stringify(posting);
}
